import _, {difference, flatten} from 'lodash';

import {buildInboxSearchUrl} from '../../helpers/models/inboxHelpers';
import {isTeamNamespace} from '../../helpers/models/namespaceHelpers';
import {AccessModel} from '../../models/api/accessModel';
import {InboxModel} from '../../models/api/inboxModel';
import {ApiSearchInboxes} from '../../models/api/searchInboxModel';
import {ThunkAction} from '../../store/appThunk';
import {getCompanyUrl} from '../../store/data/company/companySelectors';
import {getAccess} from '../../store/data/dataSelectors';
import {fetchApiAsync} from '../http/http';
import {findAccessQueue} from './specializedTaskQueues';

export function pushAccessUpdatedTask(accessUpdate: AccessModel): ThunkAction {
  return async (dispatch, getState) => {
    const queue = findAccessQueue(dispatch, getState);

    // Update the state with the new access.
    const oldAccess = getAccess(getState());
    const newAccess = {...queue.get(), ...accessUpdate};

    // Fetch inboxes given access to.
    const companyUrl = getCompanyUrl(getState());
    const inboxes = await fetchAddedInboxes(oldAccess, newAccess, companyUrl);

    const dataGroup = {access: newAccess, inboxes};

    // Wait for our turn to work.
    await queue.waitForTurnAsync();
    return queue.provideResultAsync(dataGroup);
  };
}

/*
 * Helpers.
 */

async function fetchAddedInboxes(oldAccess: AccessModel, newAccess: AccessModel, companyUrl: string) {
  const oldNamespaces = _(oldAccess.namespaces).keys().filter(isTeamNamespace).value();
  const newNamespaces = _(newAccess.namespaces).keys().filter(isTeamNamespace).value();
  const addedNamespaces = difference(newNamespaces, oldNamespaces);
  if (addedNamespaces.length === 0) {
    return [];
  }

  const fetchPromises = addedNamespaces.map((namespace) => fetchInboxesInNamespace(companyUrl, namespace));
  const fetchedInboxes = await Promise.all(fetchPromises);

  return flatten(fetchedInboxes);
}

async function fetchInboxesInNamespace(
  companyUrl: string,
  namespace: string,
  offset = 0,
): Promise<ReadonlyArray<InboxModel>> {
  const url = buildInboxSearchUrl(companyUrl, '', {
    namespace,
    limit: 500,
    offset,
  });
  const result = await fetchApiAsync(ApiSearchInboxes, url);

  if (result.inboxes.length !== result.limit) {
    // We are done fetching.
    return result.inboxes;
  }

  // Fetch the remaining inboxes.
  const nextInboxes = await fetchInboxesInNamespace(companyUrl, namespace, offset + result.limit);
  return [...result.inboxes, ...nextInboxes];
}
